import { useCallback, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Config } from '../../common/config';
import AlertModal from '../../components/alertModal';
import LeftMenu from '../../components/leftMenu';
import Pagination from '../../components/pagination';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import useStoreNo from '../../hooks/useStoreNo';
import { isSuccessResponse, getErrorMessage } from '../../components/utils';

const PAGE_SIZE = 10;

const TYPE_LABEL = { VOUCHER: '바우처', COUPON: '쿠폰', GAME: '룰렛' };
const GAME_STATE_LABEL = { READY: '대기', RUNNING: '진행중', CLOSED: '종료' };
// [2026-10-22 C2] 삭제는 빨간 외곽선으로 구분
const DANGER_STYLE = { color: '#d93025', borderColor: '#d93025' };
const STATUS_LABEL = {
  ACTIVE: '진행중',
  ENDED: '종료',
  DISABLED: '중지됨',
  SCHEDULED: '예정',
  EXPIRED: '기간종료',
};

const won = n => Number(n || 0).toLocaleString();

// DB status(ACTIVE/DISABLED/ENDED)는 그대로 두고 화면 표시만 시작일/종료일 대비 지금
// 시각으로 "예정"/"기간종료"를 계산한다(저장 안 함, 매번 계산).

// [2026-11-04] 목록에 기간을 바로 보여준다(전에는 상태 배지 툴팁에만 있었음). KST 날짜 YYYY.MM.DD
const kstDay = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  const k = new Date(t.getTime() + 9 * 3600 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${k.getUTCFullYear()}.${pad(k.getUTCMonth() + 1)}.${pad(k.getUTCDate())}`;
};
const periodText = (row) => {
  const s = kstDay(row.startDttm);
  const e = kstDay(row.endDttm);
  return s || e ? `${s || '무기한'} ~ ${e || '무기한'}` : '무기한';
};

const displayStatus = row => {
  if (row.status !== 'ACTIVE') return row.status;
  const now = new Date();
  if (row.startDttm && now < new Date(row.startDttm)) return 'SCHEDULED';
  if (row.endDttm && now > new Date(row.endDttm)) return 'EXPIRED';
  return 'ACTIVE';
};

const fmtDate = d => (d ? new Date(d).toLocaleDateString('ko-KR') : null);
// 상태 배지 마우스오버 툴팁용 — 시작일/종료일 중 하나만 있어도 표시, 둘 다 없으면 툴팁 자체를 안 띄움.
const dateRangeTitle = row => {
  const s = fmtDate(row.startDttm);
  const e = fmtDate(row.endDttm);
  return s || e ? `${s || '무기한'} ~ ${e || '무기한'}` : undefined;
};

/**
 * 상점주 프로모션 목록. 백엔드: naegift-api-promotion admin/promotion (issuerType=STORE 강제).
 * GAME 행은 회차관리 화면(gameDetail.js)으로 이동해 관리한다.
 */
// [2026-11-05] 중지 안내 — 종류별로. 쿠폰 중지는 새 발급만 멈추고 이미 받은 쿠폰은 계속 쓴다(서버 기준)
function disableMessageOf(row) {
  const name = row?.name ?? "";
  if (row?.promotionType === "COUPON") {
    const n = Number(row.usedCount || 0);
    return `"${name}" 쿠폰을 중지할까요?\n\n중지하면 새로 받기(발급)만 멈춰요.${
      n > 0 ? `\n이미 받은 손님 ${n.toLocaleString()}명은 쿠폰 기간 안에서 계속 쓸 수 있어요.` : ""
    }\n\n[재개]로 다시 진행할 수 있어요.`;
  }
  if (row?.promotionType === "VOUCHER") {
    return `"${name}" 바우처를 중지할까요?\n\n중지하면 이 할인이 걸린 기프트 판매가 멈춰요(판매중지). 이미 팔린 기프트는 그대로 쓸 수 있어요.\n\n[재개]로 다시 진행할 수 있어요.`;
  }
  return `"${name}" 프로모션을 중지할까요?\n\n중지하면 참여가 멈춰요. [재개]로 다시 진행할 수 있어요.`;
}

export default function PromotionList() {
  const navigate = useNavigate();
  const storeNo = useStoreNo();
  const { fetchData } = useAxiosFetch();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState({ headerMsg: '', mainMsg: '', isMessage: true });
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmType, setConfirmType] = useState(null); // 'disable' | 'delete'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [total, setTotal] = useState(0);
  // [2026-10-22 U1] 룰렛 행의 회차·진행상태·남은 경품 — 목록 API 에 없어 게임 상세를 행별로 읽어 캐시
  const [gameInfo, setGameInfo] = useState({});

  const load = useCallback(async pageOverride => {
    const pageNo = pageOverride !== undefined ? pageOverride : currentPage;
    setLoading(true);
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion?pageNo=${pageNo}&pageSize=${PAGE_SIZE}`,
        method: 'get',
      });
      const list = ret?.data?.data?.list;
      setRows(Array.isArray(list) ? list : []);
      setTotalPage(ret?.data?.data?.totalPage || 1);
      setTotal(ret?.data?.data?.total || 0);
    } catch (e) {
      setRows([]);
      setTotalPage(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    load(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const need = rows
      .filter(r => r.promotionType === 'GAME' && gameInfo[r.promotionNo] === undefined)
      .map(r => r.promotionNo);
    if (!need.length) return;
    let cancelled = false;
    Promise.all(
      need.map(no =>
        fetchData({ url: `${Config.API_SERVER}promotion/${no}/game`, method: 'get' })
          .then(ret => {
            const data = ret?.data?.data;
            return [no, data?.game ? { game: data.game, remain: (data.remainingPrizes || []).length } : null];
          })
          .catch(() => [no, null]),
      ),
    ).then(pairs => {
      if (!cancelled) setGameInfo(prev => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const gameRounds = r => {
    const g = gameInfo[r.promotionNo]?.game;
    return g ? `총 ${g.roundCnt}회차` : '-';
  };
  const gameProgress = r => {
    const info = gameInfo[r.promotionNo];
    const g = info?.game;
    if (!g) return info === undefined ? '…' : '-';
    if (g.gameState === 'RUNNING') return `${g.curRound}회차 진행 · 경품 ${info.remain}개 남음`;
    return GAME_STATE_LABEL[g.gameState] || g.gameState;
  };

  const pageChange = page => {
    setCurrentPage(page);
  };

  // [2026-10-21] isMessage 를 빠뜨리면 AlertModal 이 비밀번호 변경 모달(제목·규칙 문구 고정)로 그려진다 — 세 확인창 모두 명시.
  const confirmDisable = row => {
    setConfirmTarget(row);
    setConfirmType('disable');
    setModalInfo({
      headerMsg: '프로모션 중지',
      // mainMsg: `"${row.name}" 프로모션을 중지할까요? 중지하면 할인·쿠폰 사용이 멈추고, [재개]로 다시 켤 수 있습니다.`,
      // [2026-11-05] 쿠폰 중지는 새 발급만 멈춘다 — 이미 받은 쿠폰은 계속 쓴다(서버 기준과 같게)
      mainMsg: disableMessageOf(row),
      isMessage: true,
      isConfirm: true,
      isCancel: true,
    });
    setModalOpen(true);
  };

  const confirmDelete = row => {
    setConfirmTarget(row);
    setConfirmType('delete');
    setModalInfo({
      headerMsg: '프로모션 삭제',
      // mainMsg: `"${row.name}" 프로모션을 삭제합니다. 사용 이력이 있으면 삭제할 수 없습니다. 되돌릴 수 없습니다.`,
      // [2026-11-05] 삭제 가능 조건을 분명히
      mainMsg: `"${row.name}" 프로모션을 삭제합니다.\n\n아직 쓰이거나 연결되지 않은 프로모션만 삭제할 수 있어요.\n(쿠폰 발급 · 바우처 사용 · 룰렛 시작 · 룰렛 꽝 보상 연결이 있으면 삭제되지 않아요. 그땐 [중지]를 써 주세요.)\n\n삭제하면 목록에서 사라지고 되돌릴 수 없습니다.`,
      isMessage: true,
      isConfirm: true,
      isCancel: true,
    });
    setModalOpen(true);
  };

  const confirmEnable = row => {
    setConfirmTarget(row);
    setConfirmType('enable');
    setModalInfo({
      headerMsg: '프로모션 재개',
      mainMsg: `"${row.name}" 프로모션을 다시 진행할까요?`,
      isMessage: true,
      isConfirm: true,
      isCancel: true,
    });
    setModalOpen(true);
  };

  const ACTION_PATH = { disable: '/disable', enable: '/enable', delete: '' };
  const ACTION_METHOD = { disable: 'post', enable: 'post', delete: 'delete' };
  const ACTION_FAIL_MSG = {
    disable: '중지에 실패했습니다.',
    enable: '재개에 실패했습니다.',
    delete: '삭제에 실패했습니다.',
  };

  const doConfirmAction = async () => {
    const row = confirmTarget;
    const type = confirmType;
    setModalOpen(false);
    if (!row || !type) return;
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion/${row.promotionNo}${ACTION_PATH[type]}`,
        method: ACTION_METHOD[type],
      });
      if (isSuccessResponse(ret)) {
        await load();
      } else {
        // [2026-10-21] 서버가 준 실패 사유(예: "이미 사용된 프로모션은 삭제할 수 없습니다.")를 그대로 보여준다
        alert(getErrorMessage(ret) || ACTION_FAIL_MSG[type]);
      }
    } catch (e) {
      alert(ACTION_FAIL_MSG[type]);
    }
  };

  return (
    <>
      <AlertModal
        open={modalOpen}
        close={() => setModalOpen(false)}
        action={doConfirmAction}
        header={modalInfo.headerMsg}
        isConfirm={modalInfo.isConfirm}
        isCancel={modalInfo.isCancel}
        isMessage={modalInfo.isMessage}
      >
        {/* [2026-11-05] 여러 줄 안내(\n)가 한 줄로 붙지 않게 */}
        <span style={{ whiteSpace: 'pre-line' }}>{modalInfo.mainMsg}</span>
      </AlertModal>

      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>홈</NavLink>
          </li>
          <li>프로모션 관리</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="promotion" />

          <section className="rightcontant_box">
            {/* [2026-10-22 C1·C3] 등록 버튼을 목록 맨 아래에서 제목 오른쪽으로, 이름은 콘솔 3곳 통일 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="tit_01" style={{ margin: 0 }}>프로모션 관리</h2>
              <button
                type="button"
                className="button_04 button_red"
                onClick={() => navigate(`/${storeNo}/promotion-content`)}
              >
                + 프로모션 등록
              </button>
            </div>
            <p style={{ color: '#888', fontSize: 13, margin: '4px 0 16px' }}>
              상품가에 미리 반영되는 바우처, 손님이 앱 쿠폰받기에서 받아 쓰는 쿠폰, 앱 공지사항 이벤트로 진행하는 룰렛을 만듭니다.
            </p>
            {/* [2026-11-05] "왜 삭제가 안 되지?" 안내 */}
            <p style={{ color: '#888', fontSize: 13, margin: '-10px 0 16px' }}>
              삭제는 아직 쓰이거나 연결되지 않은 프로모션만 할 수 있어요(쿠폰 발급 전 · 바우처 사용 전 · 룰렛 시작 전 · 꽝 보상으로 연결되지 않은 쿠폰).
              이미 쓰였거나 연결된 프로모션은 [삭제] 버튼이 보이지 않으니 [중지]로 멈춰 주세요.
              판매 중이거나 판매할 수 있는 기프트에 걸린 바우처도 삭제가 안 돼요(그 기프트를 판매취소하면 풀려요).
              룰렛 꽝 보상으로 걸린 쿠폰은 [수정] · [중지] · [삭제]가 모두 안 돼요 — 그 룰렛 [수정]에서 꽝 보상을 빼거나 바꾸면(또는 룰렛을 삭제·종료하면) 다시 할 수 있어요.
            </p>

            {/* [2026-10-28] 상점 정보 관리 · 관리자 정보 관리처럼 테두리 카드로 */}
            <article className="form_card list_card">
            <table className="table_box_01">
              <colgroup>
                <col width="60px" />
                <col width="90px" />
                <col />
                <col width="160px" />
                <col width="140px" />
                <col width="100px" />
                <col width="270px" />
              </colgroup>
              <thead>
                <tr>
                  <th>번호</th>
                  <th>종류</th>
                  <th style={{ textAlign: 'left' }}>프로모션명</th>
                  <th>할인 / 예산</th>
                  <th>사용</th>
                  <th>상태</th>
                  <th>기간</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center' }}>불러오는 중...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center' }}>발행한 프로모션이 없습니다.</td></tr>
                ) : (
                  rows.map(r => (
                    <tr key={r.promotionNo}>
                      <td>{r.promotionNo}</td>
                      <td>
                        {/* [2026-10-28] 종류도 공지 관리처럼 색 배지 — 바우처 청록 · 쿠폰 주황 · 룰렛 보라 */}
                        <span className={`promo-kind promo-kind--${r.promotionType}`}>
                          {TYPE_LABEL[r.promotionType] || r.promotionType}
                        </span>
                      </td>
                      <td style={{ textAlign: 'left' }}>{r.name}</td>
                      <td>
                        {r.promotionType === 'COUPON'
                          ? `${won(r.couponDiscountAmount)}원 / 장`
                          : r.promotionType === 'GAME'
                            ? gameRounds(r)
                            : `${won(r.totalAmount)}원 / 상점`}
                      </td>
                      <td>
                        {/* [2026-10-21] 목록형 쿠폰의 usedCount 는 "발급 매수"라 라벨을 발급으로 표시 */}
                        {r.promotionType === 'COUPON'
                          ? `발급 ${r.usedCount ?? 0}${r.totalCount ? ` / ${r.totalCount}` : ''}`
                          : r.promotionType === 'GAME'
                            ? gameProgress(r)
                            : // `${won(r.usedAmount)}원`
                              // [2026-11-05] 잔액도 표시(S-1 ⑤) — 상점 예산 − 사용
                              `사용 ${won(r.usedAmount)}원 · 잔액 ${won(
                                Math.max(Number(r.sumTotalAmount ?? r.totalAmount ?? 0) - Number(r.usedAmount || 0), 0)
                              )}원`}
                      </td>
                      <td>
                        <span
                          className={`state-badge promo-${displayStatus(r)}`}
                          title={dateRangeTitle(r)}
                        >
                          {STATUS_LABEL[displayStatus(r)] || displayStatus(r)}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{periodText(r)}</td>
                      <td>
                        {r.promotionType === 'GAME' ? (
                          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, justifyContent: 'center', whiteSpace: 'nowrap' }}>
                            <button
                              className="button_04 button_gray"
                              onClick={() => navigate(`/${storeNo}/promotion-game?promotionNo=${r.promotionNo}`)}
                            >
                              회차관리
                            </button>
                            {r.status === 'ACTIVE' && (
                              <button
                                className="button_04 button_gray_line"
                                onClick={() => confirmDisable(r)}
                              >
                                중지
                              </button>
                            )}
                            {r.status === 'DISABLED' && (
                              <button
                                className="button_04 button_gray_line"
                                onClick={() => confirmEnable(r)}
                              >
                                재개
                              </button>
                            )}
                            {/* [2026-10-30] 시작·종료된 룰렛은 서버가 삭제를 막으므로 시작 전(READY)일 때만 보인다 */}
                            {gameInfo[r.promotionNo]?.game?.gameState === 'READY' && (
                              <button
                                className="button_04 button_gray_line"
                                style={DANGER_STYLE}
                                onClick={() => confirmDelete(r)}
                              >
                                삭제
                              </button>
                            )}
                          </div>
                        ) : (
                          r.status === 'ACTIVE' && (
                            <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, justifyContent: 'center', whiteSpace: 'nowrap' }}>
                              {/* [2026-10-20] 목록형 쿠폰 발급·발급내역 — [2026-10-22 A-F2] 번호 발급은 숨기고 받은 손님 목록만 */}
                              {r.promotionType === 'COUPON' && !r.couponCode && (
                                <button
                                  className="button_04 button_gray"
                                  onClick={() => navigate(`/${storeNo}/promotion-coupon?promotionNo=${r.promotionNo}`)}
                                >
                                  받은 손님
                                </button>
                              )}
                              {/* [2026-11-05] 룰렛 꽝 보상으로 걸린 쿠폰은 수정도 금지(서버도 막음) */}
                              {!r.hasUsage && !r.missRewardActive && (
                                <button
                                  className="button_04 button_gray"
                                  onClick={() =>
                                    navigate(`/${storeNo}/promotion-content?promotionNo=${r.promotionNo}`)
                                  }
                                >
                                  수정
                                </button>
                              )}
                              {/* [2026-11-05] 시작 전·진행 중 룰렛의 꽝 보상 쿠폰은 중지 금지(서버도 막음) */}
                              {!r.missRewardActive && (
                                <button
                                  className="button_04 button_gray_line"
                                  onClick={() => confirmDisable(r)}
                                >
                                  중지
                                </button>
                              )}
                              {/* [2026-10-21] 사용 이력이 있으면 서버가 삭제를 막으므로 버튼 자체를 숨긴다(대신 중지) */}
                              {/* [2026-11-05] 시작 전·진행 중 룰렛의 꽝 보상 쿠폰도 숨긴다 */}
                              {/* [2026-11-05] 바우처는 아직 팔 수 있는 기프트가 걸려 있으면 숨긴다 — 판매취소하면 풀림 */}
                              {!r.hasUsage && !r.missRewardActive && !(r.linkedLiveGiftCount > 0) && (
                                <button
                                  className="button_04 button_gray_line"
                                  style={DANGER_STYLE}
                                  onClick={() => confirmDelete(r)}
                                >
                                  삭제
                                </button>
                              )}
                            </div>
                          )
                        )}
                        {r.promotionType === 'VOUCHER' && r.status === 'ACTIVE' && !r.hasUsage && r.linkedLiveGiftCount > 0 && (
                          <div
                            title="이 바우처가 걸린 기프트를 판매취소하거나 바우처 연결을 빼면 삭제할 수 있어요."
                            style={{ color: '#7c3aed', fontSize: 12, marginTop: 6, textAlign: 'center' }}
                          >
                            기프트 {r.linkedLiveGiftCount}개에 연결 중
                          </div>
                        )}
                        {/* [2026-11-05] 어느 룰렛에 걸렸는지 — 버튼 아래 줄에 하나씩, 누르면 그 룰렛 회차관리로 */}
                        {r.missRewardActive && (r.missRewardGames || []).length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginTop: 6 }}>
                            {r.missRewardGames.map(g => (
                              <button
                                key={g.promotionNo}
                                type="button"
                                title="이 룰렛의 꽝 보상으로 쓰이는 중이라 수정·중지·삭제할 수 없어요. 룰렛 [수정]에서 꽝 보상을 빼면 풀려요."
                                style={{ color: '#7c3aed', fontSize: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => navigate(`/${storeNo}/promotion-game?promotionNo=${g.promotionNo}`)}
                              >
                                룰렛 #{g.promotionNo} 「{g.name}」 꽝 보상 연결 중
                              </button>
                            ))}
                          </div>
                        )}
                        {/* [2026-10-20] 진행중이 아닌 목록형 쿠폰도 발급 내역은 볼 수 있게 */}
                        {r.promotionType === 'COUPON' && !r.couponCode && r.status !== 'ACTIVE' && (
                          <button
                            className="button_04 button_gray_line"
                            onClick={() => navigate(`/${storeNo}/promotion-coupon?promotionNo=${r.promotionNo}`)}
                          >
                            받은 손님
                          </button>
                        )}
                        {r.promotionType !== 'GAME' && r.status === 'DISABLED' && (
                          <button
                            className="button_04 button_gray_line"
                            onClick={() => confirmEnable(r)}
                          >
                            재개
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <p className="table_01">전체 : {total} 건</p>

            <ul className="page_box">
              <Pagination totalPage={totalPage} onPageChange={pageChange} />
            </ul>
            </article>

          </section>
        </section>
      </main>
    </>
  );
}
